import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { DefaultApiResponse } from '../types';

export abstract class DefaultApiClient {
  private readonly backendName: string;
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  protected constructor(options: {
    backendName: string;
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.backendName = options.backendName;
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl(this.backendName);
  }

  protected async get(path: string): Promise<DefaultApiResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseBody = await response.json();

      if (response.status <= 299) {
        return {
          succeeded: true,
          error: null,
          body: responseBody,
          status: response.status,
        };
      }

      return {
        succeeded: false,
        error: this.extractErrorMessage(responseBody, response.statusText),
        body: responseBody,
        status: response.status,
      };
    } catch (error: any) {
      return {
        succeeded: false,
        error: error,
        body: null,
        status: 0,
      };
    }
  }

  protected async post<T>(path: string, body: T): Promise<DefaultApiResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const responseBody = await response.json();

      if (response.status <= 299) {
        return {
          succeeded: true,
          error: null,
          body: responseBody,
          status: response.status,
        };
      }

      return {
        succeeded: false,
        error: this.extractErrorMessage(responseBody, response.statusText),
        body: responseBody,
        status: response.status,
      };
    } catch (error: any) {
      return {
        succeeded: false,
        error: error,
        body: null,
        status: 0,
      };
    }
  }

  protected async put<T>(path: string, body: T): Promise<DefaultApiResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const responseBody = await response.json();

      if (response.status <= 299) {
        return {
          succeeded: true,
          error: null,
          body: responseBody,
          status: response.status,
        };
      }

      return {
        succeeded: false,
        error: this.extractErrorMessage(responseBody, response.statusText),
        body: responseBody,
        status: response.status,
      };
    } catch (error: any) {
      return {
        succeeded: false,
        error: error,
        body: null,
        status: 0,
      };
    }
  }

  protected async delete(path: string): Promise<DefaultApiResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseBody = await response.json();

      if (response.status <= 299) {
        return {
          succeeded: true,
          error: null,
          body: responseBody,
          status: response.status,
        };
      }

      return {
        succeeded: false,
        error: this.extractErrorMessage(responseBody, response.statusText),
        body: responseBody,
        status: response.status,
      };
    } catch (error: any) {
      return {
        succeeded: false,
        error: error,
        body: null,
        status: 0,
      };
    }
  }

  // Backend error bodies come in several shapes ({error: {message}}, {message},
  // non-JSON proxies); a blind `responseBody.error.message` would throw.
  private extractErrorMessage(
    responseBody: any,
    fallback: string,
  ): string {
    return (
      responseBody?.error?.message ??
      responseBody?.message ??
      fallback
    );
  }
}
